import React, { useState, useEffect, useContext } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import useApi from '../../hooks/useApi';
import { format, subDays } from 'date-fns';
import { ExclamationTriangleIcon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import BranchDropdown from '../../components/BranchDropdown';

const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '0';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(number);
};

function IncompleteVisitsCard() {
  const { uinfo } = useContext(GlobalContext);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [isExpanded, setIsExpanded] = useState(false);

  // Get yesterday's date
  const yesterday = subDays(new Date(), 1);
  const yesterdayStr = format(yesterday, 'yyyy-MM-dd');

  // Fetch visits from yesterday
  const { data: visitsData, isLoading } = useApi({
    url: `/visiondashboard/repvisits?start_date=${yesterdayStr}&end_date=${yesterdayStr}`,
    initial: { data: [] }
  });

  // Get all operators/reps
  const { data: operators } = useApi({
    url: '/salesassist/cplusassist/getOperators',
    initial: []
  });

  // Process visits data
  const processedData = React.useMemo(() => {
    if (!visitsData?.data || !Array.isArray(visitsData.data)) {
      return {
        incompleteVisits: [],
        repStats: []
      };
    }

    const visits = visitsData.data;
    
    // Filter incomplete visits (status is not 'completed')
    const incompleteVisits = visits.filter(v => {
      const status = (v.status || '').toLowerCase();
      return status !== 'completed' && status !== 'cancelled';
    });

    // Group by rep and calculate stats
    const repMap = {};
    
    visits.forEach(visit => {
      const repCode = visit.rep_code || 'Unknown';
      if (!repMap[repCode]) {
        const operator = operators?.find(op => 
          (op.Inits || op.inits || op.Name) === repCode
        );
        repMap[repCode] = {
          repCode,
          repName: operator ? `${operator.FName || ''} ${operator.SName || ''}`.trim() || operator.Name : repCode,
          branch: visit.branch || operator?.Branch || 'Unknown',
          totalVisits: 0,
          completedVisits: 0,
          incompleteVisits: 0
        };
      }
      
      repMap[repCode].totalVisits++;
      const status = (visit.status || '').toLowerCase();
      if (status === 'completed') {
        repMap[repCode].completedVisits++;
      } else if (status !== 'cancelled') {
        repMap[repCode].incompleteVisits++;
      }
    });

    const repStats = Object.values(repMap)
      .filter(rep => rep.incompleteVisits > 0)
      .map(rep => ({
        ...rep,
        completionPercent: rep.totalVisits > 0 
          ? Math.round((rep.completedVisits / rep.totalVisits) * 100) 
          : 0
      }))
      .sort((a, b) => b.incompleteVisits - a.incompleteVisits);

    // Filter by branch if selected
    const filteredRepStats = selectedBranch === 'all' 
      ? repStats 
      : repStats.filter(rep => rep.branch === selectedBranch);

    const filteredIncomplete = selectedBranch === 'all'
      ? incompleteVisits
      : incompleteVisits.filter(v => {
          const repCode = v.rep_code || 'Unknown';
          const operator = operators?.find(op => 
            (op.Inits || op.inits || op.Name) === repCode
          );
          return (v.branch || operator?.Branch) === selectedBranch;
        });

    return {
      incompleteVisits: filteredIncomplete,
      repStats: filteredRepStats,
      totalIncomplete: filteredIncomplete.length,
      totalReps: filteredRepStats.length
    };
  }, [visitsData, operators, selectedBranch]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Card Header */}
      <div 
        className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Rep Visits Incomplete Yesterday</h3>
              <p className="text-sm text-gray-600 mt-1">
                {processedData.totalIncomplete} incomplete visit{processedData.totalIncomplete !== 1 ? 's' : ''} from {format(yesterday, 'dd/MM/yyyy')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-2xl font-bold text-orange-600">
                {processedData.totalReps}
              </div>
              <div className="text-xs text-gray-500">Reps affected</div>
            </div>
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          {/* Branch Filter */}
          <div className="mb-4">
            <BranchDropdown
              branch={selectedBranch}
              setBranch={setSelectedBranch}
              placeholder="Filter by branch"
              clearable
            />
          </div>

          {/* Rep Stats Table */}
          {processedData.repStats.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p>All visits completed yesterday!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rep Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Planned
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Incomplete
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completion %
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {processedData.repStats.map((rep, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rep.repName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {rep.branch}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-900">
                        {rep.totalVisits}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-green-600 font-semibold">
                        {rep.completedVisits}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-red-600 font-semibold">
                        {rep.incompleteVisits}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                        <span className={clsx(
                          'px-2 py-1 rounded-full text-xs font-semibold',
                          rep.completionPercent >= 80 ? 'bg-green-100 text-green-800' :
                          rep.completionPercent >= 50 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        )}>
                          {rep.completionPercent}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default IncompleteVisitsCard;

